
import React from 'react';
import { Spin, Tooltip } from 'antd';
import { useSearchParams } from 'react-router-dom';
import {
    PlusOutlined,
    FlagOutlined,
    EditOutlined,
    DeleteOutlined,
    LeftOutlined,
    RightOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import MultipleButton from '@/components/ui/MultipleButton';
import { SliderTab, ScrollWrapper, ScrollArrow } from '@/components/ui/SliderTab';
import {
    useProjectSprintsTab,
    CREATE_SPRINT_DISABLED_TOOLTIP,
} from '@hooks/projects/useProjectSprints';
import SprintKanbanBoard from '@/components/module/projects/sprints_tab/SprintKanbanBoard';
import SprintKanbanBoardShimmer from '@/components/module/projects/sprints_tab/SprintKanbanBoardShimmer';
import TaskDetailModal from '@/components/module/projects/task/TaskDetailModal';
import SprintsTabModals from '@/components/module/projects/sprints_tab/SprintsTabModals';
import SprintTaskAssignUserModal from '@/components/module/projects/sprints_tab/SprintTaskAssignUserModal';
import SprintSubmitToQAModal from '@/components/module/projects/sprints_tab/SprintSubmitToQAModal';
import TaskConsultationModal from '@/components/module/projects/sprints_tab/TaskConsultationModal';
import FloatingSelectionBar from '@/components/ui/FloatingSelectionBar';
import { formatDate, getMilestoneDisplayLabel, getSprintDisplayLabel, getSprintProgressPercent, getSprintTaskApiId } from '@hooks/projects/projectHelpers';
import {
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
    DetailPanelLoading,
    BoardKanbanBoardShell,
    BoardKanbanFetchingStrip,
    BoardTabRoot,
    BoardPageTitle,
    BoardPageSubtitle,
    BoardPillTabRow,
    BoardPillTab,
    BoardPillTabLabel,
    BoardUnderlineTabRow,
    BoardUnderlineTabInner,
    BoardUnderlineTab,
    BoardUnderlineTabLine,
    BoardUnderlineTabPrimaryLabel,
    BoardUnderlineTabPercent,
    MutedText,
} from '../style';

const MilestoneSvgIcon = ({ size = 32, ...props }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...props}
    >
        <line x1="4" y1="22" x2="20" y2="22" />
        <line x1="12" y1="2" x2="12" y2="22" />
        <path d="M12 2h8l-2 4 2 4h-8" fill="currentColor" fillOpacity="0.1" />
        <circle cx="12" cy="16" r="3" fill="currentColor" fillOpacity="0.2" />
    </svg>
);

const PhaseSvgIcon = ({ size = 32, ...props }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...props}
    >
        <path d="M2 12h4l3-6 4 12 3-6h4" />
        <circle cx="6" cy="12" r="2" fill="currentColor" />
        <circle cx="12" cy="12" r="2" fill="currentColor" />
        <circle cx="18" cy="12" r="2" fill="currentColor" />
    </svg>
);

export default function ProjectSprintsTab({
    projectId,
    project: initialProject,
    createSprintModalOpen: createSprintModalOpenProp,
    setCreateSprintModalOpen: setCreateSprintModalOpenProp,
}) {
    const {
        milestones,
        hasMilestones,
        hasSprints,
        useSprintsEnabled,
        isTabLoading,
        isSprintKanbanFetching,
        goToMilestonesTab,
        modals,
        kanban,
        celebratingTaskKey,
        setCelebratingTaskKey,
        handleMarkTaskDone,
        handleRevertToTodo,
        handleColumnFlowAction,
        canCreateSprint,
        canEditSprint,
        canDeleteSprint,
        canCreateTask,
        canEditTask,
        canDeleteTask,
        canMarkTaskTodo,
        canAssignTask,
        taskForAssignModal,
        taskAssignPicklistUsers,
        openTaskAssignModal,
        closeTaskAssignModal,
        submitTaskAssign,
        assignTaskPending,
        sprintTaskDetail,
        openSprintTaskDetail,
        openSprintTaskForEdit,
        closeSprintTaskDetail,
        refreshSprintTaskDetail,
        sprintTaskDetailMarkdown,
        sprintTaskDetailBugs,
        handleDeleteSprintTask,
        handleDeleteSprint,
        deleteSprintPending,
        submissionPanelOpen,
        closeSubmissionPanel,
        submissionNotes,
        setSubmissionNotes,
        submissionPendingFiles,
        submissionPendingLinks,
        submissionLinkModalOpen,
        submissionLinkForm,
        handleSubmissionFilesSelected,
        handleSubmissionLinkRequest,
        handleSubmissionLinkModalSubmit,
        closeSubmissionLinkModal,
        removeSubmissionPendingFile,
        removeSubmissionPendingLink,
        handleSubmissionEditorLinkRemoved,
        submitToQA,
        submitToQAPending,
        removeFromSubmission,
        submissionGroupedBySprintEnriched,
        submissionSession,
        sprintSubmittedSubmissionGroups,
        sprintSubmittedOrphanTasks,
        consultation,
        project,
        sprints,
        phases,
        usePhasesEnabled,
        useMilestonesEnabled,
        isQAEnabled,
        isPMOEnabled,
        submittedTo,
        setSubmittedTo,
    } = useProjectSprintsTab(projectId, {
        project: initialProject,
        createSprintModalOpen: createSprintModalOpenProp,
        setCreateSprintModalOpen: setCreateSprintModalOpenProp,
    });

    const [searchParams] = useSearchParams();
    const isTasksOnlyMode = searchParams.get('tab') === 'tasks' || searchParams.get('tab') === 'task';

    const {
        selectedMilestoneId,
        setSelectedMilestoneId,
        selectedSprintId,
        setSelectedSprintId,
        sprintsForMilestone,
        selectedSprint,
        todoTasks,
        completedTasks,
        consultationTasks,
        submittedTasks,
        dropTarget,
        draggingColumn,
        clearDropTarget,
        handleDndDragStart,
        handleDndDragOverColumn,
        handleDndDragEnd,
        onRequestSubmitToQA,
    } = kanban;

    const sprintTabsScrollRef = React.useRef(null);
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);
    const [sprintDetailRequestKey, setSprintDetailRequestKey] = React.useState(0);

    const updateSprintTabsScrollState = React.useCallback(() => {
        const el = sprintTabsScrollRef.current;
        if (!el) return;
        const maxLeft = el.scrollWidth - el.clientWidth;
        const left = el.scrollLeft;
        setCanScrollLeft(left > 1);
        setCanScrollRight(maxLeft - left > 1);
    }, []);

    React.useEffect(() => {
        updateSprintTabsScrollState();
        const onResize = () => updateSprintTabsScrollState();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [updateSprintTabsScrollState]);

    React.useEffect(() => {
        // When list/selection changes, recompute scroll affordances.
        updateSprintTabsScrollState();
    }, [sprintsForMilestone, selectedSprintId, updateSprintTabsScrollState]);

    // Completed column batch PMO selection states
    const [completedPmoSelectMode, setCompletedPmoSelectMode] = React.useState(false);
    const [completedPmoSelectedIds, setCompletedPmoSelectedIds] = React.useState([]);

    const getTaskMatchedSubmission = React.useCallback((task, qaSubmissionsList) => {
        const cameFromQa = Boolean(task?.cameFromQa || task?.came_from_qa);
        if (!cameFromQa || !Array.isArray(qaSubmissionsList)) return null;
        const taskId = getSprintTaskApiId(task) ?? task?.id ?? task?.taskId;
        if (taskId == null) return null;
        return qaSubmissionsList.find(sub =>
            Array.isArray(sub?.tasks) && sub.tasks.some(tId => String(tId) === String(taskId))
        );
    }, []);

    const firstSelectedTask = React.useMemo(() => {
        if (completedPmoSelectedIds.length === 0) return null;
        return completedTasks.find(t => {
            const tid = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
            return completedPmoSelectedIds.map(String).includes(String(tid));
        });
    }, [completedTasks, completedPmoSelectedIds]);

    const filteredCompletedTasks = React.useMemo(() => {
        if (!completedPmoSelectMode || !firstSelectedTask) {
            return completedTasks;
        }
        const qaSubmissions = selectedSprint?.qaSubmissions || [];
        const firstCameFromQa = Boolean(firstSelectedTask?.cameFromQa || firstSelectedTask?.came_from_qa);
        
        if (firstCameFromQa) {
            const matchedSub = getTaskMatchedSubmission(firstSelectedTask, qaSubmissions);
            const subId = matchedSub?.submissionId || matchedSub?.id;
            
            return completedTasks.filter(t => {
                const tCameFromQa = Boolean(t?.cameFromQa || t?.came_from_qa);
                if (!tCameFromQa) return false;
                const tSub = getTaskMatchedSubmission(t, qaSubmissions);
                const tSubId = tSub?.submissionId || tSub?.id;
                return tSubId != null && subId != null && String(tSubId) === String(subId);
            });
        } else {
            return completedTasks.filter(t => {
                const tCameFromQa = Boolean(t?.cameFromQa || t?.came_from_qa);
                return !tCameFromQa;
            });
        }
    }, [completedTasks, completedPmoSelectMode, firstSelectedTask, selectedSprint, getTaskMatchedSubmission]);

    const handleCompletedPmoToggleTask = React.useCallback((taskId) => {
        setCompletedPmoSelectedIds(prev => {
            const isSelected = prev.map(String).includes(String(taskId));
            if (isSelected) {
                return prev.filter(id => String(id) !== String(taskId));
            } else {
                return [...prev, taskId];
            }
        });
    }, []);

    const handleCompletedPmoCancelSelectMode = React.useCallback(() => {
        setCompletedPmoSelectMode(false);
        setCompletedPmoSelectedIds([]);
    }, []);

    const handleCompletedPmoSelectAll = React.useCallback(() => {
        const targetTasks = filteredCompletedTasks;
        const allIds = targetTasks.map(t => getSprintTaskApiId(t) ?? t?.id ?? t?.taskId).filter(id => id != null);
        setCompletedPmoSelectedIds(allIds);
    }, [filteredCompletedTasks]);

    const handleCompletedPmoUnselectAll = React.useCallback(() => {
        setCompletedPmoSelectedIds([]);
    }, []);

    const handleCompletedPmoStartSelectMode = React.useCallback(() => {
        setCompletedPmoSelectMode(true);
        setCompletedPmoSelectedIds([]);
    }, []);

    // Wrap handleDndDragEnd to handle multi-task move from completed to submitted
    const handleDndDragEndWrapped = React.useCallback((dragPayload) => {
        if (
            dragPayload.sourceColumn === 'completed' &&
            dragPayload.targetColumn === 'submitted' &&
            completedPmoSelectMode &&
            completedPmoSelectedIds.length > 0
        ) {
            dragPayload.selectedTaskIds = completedPmoSelectedIds;
            setCompletedPmoSelectMode(false);
            setCompletedPmoSelectedIds([]);
        }
        handleDndDragEnd(dragPayload);
    }, [handleDndDragEnd, completedPmoSelectMode, completedPmoSelectedIds]);

    const scrollSprintTabsBy = React.useCallback(
        (dir) => {
            const el = sprintTabsScrollRef.current;
            if (!el) return;
            const delta = Math.max(240, Math.floor(el.clientWidth * 0.8)) * (dir === 'left' ? -1 : 1);
            el.scrollBy({ left: delta, behavior: 'smooth' });
            // state will update on scroll, but schedule a follow-up in case of no scroll event
            requestAnimationFrame(updateSprintTabsScrollState);
        },
        [updateSprintTabsScrollState],
    );

    const shouldShowEmptyState = !hasMilestones && (useMilestonesEnabled || usePhasesEnabled) && (!isTasksOnlyMode || !useSprintsEnabled);

    const mainContent = (
        <BoardTabRoot
            style={{
                flex: 1,
                minWidth: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                paddingRight: 0,
                backgroundColor: 'transparent',
            }}
        >
            {shouldShowEmptyState ? (
                useMilestonesEnabled ? (
                    <EmptyStateBlock
                        style={{
                            marginBottom: 24,
                            flexShrink: 0,
                            padding: '20px 24px',
                            alignItems: 'stretch',
                            textAlign: 'left',
                        }}
                    >
                        <EmptyStateTitle style={{ marginBottom: 8 }}>Create a milestone first</EmptyStateTitle>
                        <EmptyStateDescription style={{ marginBottom: 16, maxWidth: '100%' }}>
                            {useSprintsEnabled
                                ? 'Sprints belong to a milestone. Add at least one milestone in the Milestones tab, then you can create sprints here.'
                                : 'Tasks belong to a milestone. Add at least one milestone in the Milestones tab, then you can create tasks here.'}
                        </EmptyStateDescription>
                        <Button
                            type="button"
                            $primary
                            $blackOnLight
                            $size="13px"
                            $padding="8px 16px"
                            onClick={goToMilestonesTab}
                        >
                            Go to Milestones
                        </Button>
                    </EmptyStateBlock>
                ) : (
                    usePhasesEnabled ? (
                        <EmptyStateBlock
                            style={{
                                marginBottom: 24,
                                flexShrink: 0,
                                padding: '20px 24px',
                                alignItems: 'stretch',
                                textAlign: 'left',
                            }}
                        >
                            <EmptyStateIcon style={{ marginBottom: 12 }}>
                                <PhaseSvgIcon size={36} style={{ color: 'var(--color-primary, #3b82f6)' }} />
                            </EmptyStateIcon>
                            <EmptyStateTitle style={{ marginBottom: 8 }}>Create a phase first</EmptyStateTitle>
                            <EmptyStateDescription style={{ marginBottom: 16, maxWidth: '100%' }}>
                                {useSprintsEnabled
                                    ? 'Sprints belong to a phase. Add at least one phase in the Phases tab, then you can create sprints here.'
                                    : 'Tasks belong to a phase. Add at least one phase in the Phases tab, then you can create tasks here.'}
                            </EmptyStateDescription>
                            <Button
                                type="button"
                                $primary
                                $blackOnLight
                                $size="13px"
                                $padding="8px 16px"
                                onClick={goToMilestonesTab}
                            >
                                Go to Phases
                            </Button>
                        </EmptyStateBlock>
                    ) : null
                )
            ) : (
                <>

            {/* Show Milestone Tabs */}
            {useMilestonesEnabled && milestones.length > 0 && (!isTasksOnlyMode || !useSprintsEnabled) && (
                <Container $marginBottom="24px" $flexShrink="0">
                    <SliderTab
                        items={milestones.map((m) => ({
                            key: m.id,
                            label: getMilestoneDisplayLabel(m) || m.name || 'Milestone',
                        }))}
                        activeKey={selectedMilestoneId}
                        onChange={(key) => {
                            const milestone = milestones.find((m) => String(m.id) === String(key));
                            if (milestone) setSelectedMilestoneId(milestone.id);
                        }}
                        fitContent
                    />
                </Container>
            )}

            {/* Show Phase Tabs */}
            {!useMilestonesEnabled && usePhasesEnabled && phases.length > 0 && (!isTasksOnlyMode || !useSprintsEnabled) && (
                <Container $marginBottom="24px" $flexShrink="0">
                    <SliderTab
                        items={phases.map((p) => ({
                            key: p.id,
                            label: p.title || 'Phase',
                        }))}
                        activeKey={selectedMilestoneId}
                        onChange={(key) => {
                            const phase = phases.find((p) => String(p.id) === String(key));
                            if (phase) setSelectedMilestoneId(phase.id);
                        }}
                        fitContent
                    />
                </Container>
            )}

            <Container
                $display="flex"
                $justifyContent="space-between"
                $alignItems="flex-start"
                $gap="16px"
                $flexWrap="wrap"
                style={{ marginBottom: 16 }}
            >
                <div>
                    <BoardPageTitle>{isTasksOnlyMode ? 'Project Tasks' : 'Project Sprints'}</BoardPageTitle>
                    <BoardPageSubtitle>
                        {isTasksOnlyMode ? 'Manage project tasks here.' : 'Create sprints of projects and manage tasks here.'}
                    </BoardPageSubtitle>
                </div>
                {!isTasksOnlyMode && (
                    <Container
                        $display="flex"
                        $alignItems="center"
                        $gap="8px"
                        $flexWrap="wrap"
                        style={{ marginRight: 24 }}
                    >
                        <Button
                            type="button"
                            $primary
                            $blackOnLight
                            $size="13px"
                            $padding="8px 16px"
                            icon={<PlusOutlined />}
                            permissionDenied={!canCreateSprint}
                            disabled={!hasMilestones}
                            disabledTooltip={
                                canCreateSprint && !hasMilestones
                                    ? (useMilestonesEnabled
                                        ? 'Add at least one milestone in the Milestones tab, then you can create sprints here.'
                                        : 'Add at least one phase in the Phases tab, then you can create sprints here.')
                                    : undefined
                            }
                            onClick={() => modals.openCreateSprintModal()}
                        >
                            Create Sprint
                        </Button>
                    </Container>
                )}
            </Container>

            {sprintsForMilestone.length > 0 ? (
                <>
                {!isTasksOnlyMode && (
                <Container
                    $display="flex"
                    $alignItems="center"
                    $justifyContent="space-between"
                    $gap="12px"
                    style={{ marginBottom: 8, marginTop: 0, minWidth: 0 }}
                >
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <ScrollWrapper>
                            {canScrollLeft && (
                                <ScrollArrow type="button" $left onClick={(e) => {
                                    e.stopPropagation();
                                    scrollSprintTabsBy('left');
                                }}>
                                    <LeftOutlined />
                                </ScrollArrow>
                            )}

                            <BoardUnderlineTabRow style={{ marginBottom: 0, marginTop: 0 }}>
                                <BoardUnderlineTabInner
                                    ref={sprintTabsScrollRef}
                                    className="sprint-tabs-scroll"
                                    onScroll={updateSprintTabsScrollState}
                                    style={{ scrollbarWidth: 'none' }}
                                >
                                    {sprintsForMilestone.map((s) => {
                                        const active = String(selectedSprintId) === String(s.id);
                                        const pct = getSprintProgressPercent(s);
                                        const label = getSprintDisplayLabel(s);
                                        return (
                                            <BoardUnderlineTab
                                                key={s.id}
                                                type="button"
                                                $active={active}
                                                onClick={() => setSelectedSprintId(s.id)}
                                                title={`${label} ${pct}%`}
                                            >
                                                <BoardUnderlineTabLine>
                                                    <BoardUnderlineTabPrimaryLabel $active={active}>
                                                        {label}
                                                    </BoardUnderlineTabPrimaryLabel>
                                                    {active ? (
                                                        <Tooltip
                                                            title="Sprint Detail"
                                                            placement="top"
                                                            color="rgba(17,24,39,0.96)"
                                                            mouseEnterDelay={0.05}
                                                            styles={{
                                                                body: {
                                                                    fontSize: 11,
                                                                    fontWeight: 600,
                                                                    lineHeight: 1.1,
                                                                    borderRadius: 7,
                                                                    padding: '6px 9px',
                                                                }
                                                            }}
                                                        >
                                                            <span
                                                                role="button"
                                                                tabIndex={0}
                                                                className="sprint-tab-info-trigger"
                                                                aria-label="Sprint Detail"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSprintDetailRequestKey((prev) => prev + 1);
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        setSprintDetailRequestKey((prev) => prev + 1);
                                                                    }
                                                                }}
                                                            >
                                                                <InfoCircleOutlined style={{ fontSize: 14 }} />
                                                            </span>
                                                        </Tooltip>
                                                    ) : null}
                                                    <BoardUnderlineTabPercent $active={active}>
                                                        {pct}%
                                                    </BoardUnderlineTabPercent>
                                                </BoardUnderlineTabLine>
                                            </BoardUnderlineTab>
                                        );
                                    })}
                                </BoardUnderlineTabInner>
                            </BoardUnderlineTabRow>

                            {canScrollRight && (
                                <ScrollArrow type="button" $right onClick={(e) => {
                                    e.stopPropagation();
                                    scrollSprintTabsBy('right');
                                }}>
                                    <RightOutlined />
                                </ScrollArrow>
                            )}
                        </ScrollWrapper>
                    </div>

                </Container>
                )}
                {isSprintKanbanFetching && selectedSprintId ? (
                    <BoardKanbanFetchingStrip aria-hidden />
                ) : null}
                </>
            ) : (
                // Only show "no sprints" message in the Sprints view, not Tasks view.
                // In tasks-only mode the kanban always renders (virtual no-sprint entry).
                !isTasksOnlyMode && (
                    <Container style={{ padding: '40px 24px', textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <EmptyStateBlock>
                            <EmptyStateIcon>
                                <FlagOutlined />
                            </EmptyStateIcon>
                            <EmptyStateTitle>No Sprints Found</EmptyStateTitle>
                            <EmptyStateDescription>
                                {hasMilestones && milestones.length > 0
                                    ? 'There are no sprints in this milestone yet. Create one to start managing tasks.'
                                    : hasMilestones
                                        ? 'There are no sprints yet. Create one to start managing tasks.'
                                        : 'Create a milestone first, then add sprints to manage your tasks.'}
                            </EmptyStateDescription>
                        </EmptyStateBlock>
                    </Container>
                )
            )}

            {selectedSprint && (isTasksOnlyMode || sprintsForMilestone.length > 0) && (
                <BoardKanbanBoardShell>
                    {isSprintKanbanFetching ? (
                        <SprintKanbanBoardShimmer />
                    ) : (
                    <Container
                        $display="flex"
                        $flexDirection="column"
                        $flex="1"
                        $minHeight="0"
                        $overflow="hidden"
                        style={{ width: '100%' }}
                    >
                        <SprintKanbanBoard
                            sprint={selectedSprint}
                            sprintTitle={isTasksOnlyMode ? "" : getSprintDisplayLabel(selectedSprint)}
                            canEditSprint={isTasksOnlyMode ? false : canEditSprint}
                            canDeleteSprint={isTasksOnlyMode ? false : canDeleteSprint}
                            deleteSprintPending={deleteSprintPending}
                            onEditSprint={() => modals.openEditSprintModal(selectedSprint)}
                            onDeleteSprint={() => handleDeleteSprint(selectedSprint)}
                            formatDateFn={formatDate}
                            todoTasks={todoTasks}
                            completedTasks={filteredCompletedTasks}
                            consultationTasks={consultationTasks}
                            submittedTasks={submittedTasks}
                            submittedSubmissionGroups={sprintSubmittedSubmissionGroups}
                            orphanSubmittedTasks={sprintSubmittedOrphanTasks}
                            dropTarget={dropTarget}
                            draggingColumn={draggingColumn}
                            onAddTask={modals.openTaskModal}
                            onConsultationCreate={consultation.openConsultationModal}
                            onMarkTaskDone={handleMarkTaskDone}
                            onRevertToTodo={handleRevertToTodo}
                            celebratingTaskKey={celebratingTaskKey}
                            onCelebrationComplete={() => setCelebratingTaskKey(null)}
                            onClearDrop={clearDropTarget}
                            onDndDragStart={handleDndDragStart}
                            onDndDragOverColumn={handleDndDragOverColumn}
                            onDndDragEnd={handleDndDragEndWrapped}
                            onTaskOpenDetail={openSprintTaskDetail}
                            onTaskEdit={openSprintTaskForEdit}
                            onTaskDelete={handleDeleteSprintTask}
                            canCreateTask={canCreateTask}
                            canEditTask={canEditTask}
                            canDeleteTask={canDeleteTask}
                            canMarkTaskTodo={canMarkTaskTodo}
                            canAssignTask={canAssignTask}
                            onTaskAssign={openTaskAssignModal}
                            onColumnFlowAction={handleColumnFlowAction}
                            openSprintDetailRequestKey={sprintDetailRequestKey}
                            completedPmoSelectMode={completedPmoSelectMode}
                            completedPmoSelectedIds={completedPmoSelectedIds}
                            onCompletedPmoStartSelectMode={handleCompletedPmoStartSelectMode}
                            onCompletedPmoCancelSelectMode={handleCompletedPmoCancelSelectMode}
                            onCompletedPmoSelectAll={handleCompletedPmoSelectAll}
                            onCompletedPmoUnselectAll={handleCompletedPmoUnselectAll}
                            onCompletedPmoToggle={handleCompletedPmoToggleTask}
                        />
                    </Container>
                    )}
                </BoardKanbanBoardShell>
            )}
            {completedPmoSelectMode && (
                <FloatingSelectionBar
                    selectedCount={completedPmoSelectedIds.length}
                    onSubmitSelection={() => {
                        onRequestSubmitToQA?.({
                            sprintId: selectedSprintId,
                            selectedTaskIds: completedPmoSelectedIds,
                        });
                        setCompletedPmoSelectMode(false);
                        setCompletedPmoSelectedIds([]);
                    }}
                    onSubmitAll={() => {
                        const allCompletedIds = completedTasks.map(t => getSprintTaskApiId(t) ?? t?.id ?? t?.taskId).filter(id => id != null);
                        onRequestSubmitToQA?.({
                            sprintId: selectedSprintId,
                            selectedTaskIds: allCompletedIds,
                        });
                        setCompletedPmoSelectMode(false);
                        setCompletedPmoSelectedIds([]);
                    }}
                    onCancel={handleCompletedPmoCancelSelectMode}
                    submitAllLabel="Submit All Completed"
                    totalCount={completedTasks.length}
                />
            )}
                </>
            )}
        </BoardTabRoot>
    );

    const useSprints = project?.capabilities?.useSprints ?? true;
    const useTasks = project?.capabilities?.useTasks ?? true;

    if (!useSprints && !isTasksOnlyMode) {
        return (
            <Container style={{ padding: '40px 24px', textAlign: 'center', flex: 1 }}>
                <EmptyStateBlock>
                    <EmptyStateIcon>
                        <FlagOutlined />
                    </EmptyStateIcon>
                    <EmptyStateTitle>Sprints Disabled</EmptyStateTitle>
                    <EmptyStateDescription>
                        Sprints are disabled according to the capabilities of this project type.
                    </EmptyStateDescription>
                </EmptyStateBlock>
            </Container>
        );
    }

    if (!useTasks) {
        return (
            <Container style={{ padding: '40px 24px', textAlign: 'center', flex: 1 }}>
                <EmptyStateBlock>
                    <EmptyStateIcon>
                        <InfoCircleOutlined />
                    </EmptyStateIcon>
                    <EmptyStateTitle>Tasks Disabled</EmptyStateTitle>
                    <EmptyStateDescription>
                        Tasks are disabled according to the capabilities of this project type.
                    </EmptyStateDescription>
                </EmptyStateBlock>
            </Container>
        );
    }

    return (
        <>
            {isTabLoading ? (
                <Container
                    style={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <EmptyStateBlock>
                        <DetailPanelLoading>
                            <Spin size="large" />
                        </DetailPanelLoading>
                    </EmptyStateBlock>
                </Container>
            ) : (
                <Container
                    $display="flex"
                    $flex="1"
                    $minHeight="0"
                    $overflow="hidden"
                    style={{ marginBottom: 0, paddingBottom: 0 }}
                >
                    {mainContent}
                </Container>
            )}
            <SprintsTabModals {...modals} />
            <SprintTaskAssignUserModal
                open={Boolean(taskForAssignModal)}
                onCancel={closeTaskAssignModal}
                task={taskForAssignModal}
                users={taskAssignPicklistUsers}
                onSubmit={submitTaskAssign}
                submitPending={assignTaskPending}
            />
            <TaskDetailModal
                projectId={projectId}
                open={Boolean(sprintTaskDetail?.task && sprintTaskDetail?.sprint)}
                onClose={closeSprintTaskDetail}
                onRequestRefresh={refreshSprintTaskDetail}
                task={sprintTaskDetail?.task ?? null}
                taskDescriptionMarkdown={sprintTaskDetailMarkdown}
                taskBugs={sprintTaskDetailBugs}
                mentionUsers={modals.mentionUsers}
                mentionMilestones={modals.mentionMilestones}
                mentionSprints={modals.mentionSprints}
                readOnlyBugActions
            />
            <SprintSubmitToQAModal
                open={submissionPanelOpen}
                onCancel={closeSubmissionPanel}
                submissionGroupedBySprintEnriched={submissionGroupedBySprintEnriched}
                submissionSession={submissionSession}
                submissionNotes={submissionNotes}
                setSubmissionNotes={setSubmissionNotes}
                mentionUsers={modals.mentionUsers}
                mentionMilestones={modals.mentionMilestones}
                mentionSprints={modals.mentionSprints}
                submissionPendingFiles={submissionPendingFiles}
                submissionPendingLinks={submissionPendingLinks}
                handleSubmissionFilesSelected={handleSubmissionFilesSelected}
                handleSubmissionLinkRequest={handleSubmissionLinkRequest}
                handleSubmissionLinkModalSubmit={handleSubmissionLinkModalSubmit}
                submissionLinkModalOpen={submissionLinkModalOpen}
                closeSubmissionLinkModal={closeSubmissionLinkModal}
                submissionLinkForm={submissionLinkForm}
                removeSubmissionPendingFile={removeSubmissionPendingFile}
                removeSubmissionPendingLink={removeSubmissionPendingLink}
                handleSubmissionEditorLinkRemoved={handleSubmissionEditorLinkRemoved}
                submitToQA={submitToQA}
                submitToQAPending={submitToQAPending}
                removeFromSubmission={removeFromSubmission}
                onOpenTaskDetail={openSprintTaskDetail}
                isQAEnabled={isQAEnabled}
                isPMOEnabled={isPMOEnabled}
                submittedTo={submittedTo}
                setSubmittedTo={setSubmittedTo}
            />
            <TaskConsultationModal
                projectId={projectId}
                projectMembers={project?.assignedUsers || []}
                allSprints={selectedSprint ? [selectedSprint] : []}

                addToConsultation={consultation.addToConsultation}
                {...consultation}
                onOpenTaskDetail={openSprintTaskDetail}
                mentionUsers={modals.mentionUsers}
                mentionMilestones={modals.mentionMilestones}
                mentionSprints={modals.mentionSprints}
            />
        </>
    );
}
